import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { rmTimes, rmTimeRec, invalidDates, rmTransEligible } from 'src/models';
import { AxiosError } from 'axios';
import { IStoreState } from 'src/store';
import { ViewMode } from 'src/enums';
import * as acBase from 'src/store/states/GenericActionCreator';

/** Interfaces */
export interface IrmTimesState {
    rmTimes: rmTimes;
    rmTimeSlots: rmTimeRec[];
    rmTransEligible: rmTransEligible;
    invalidDates: invalidDates[];
    invalidDatesDept: string;
    error?: string | undefined;
}

export interface rmTimesRtn {
    rmTimes: rmTimes;
    rmTimeSlots: rmTimeRec[];
    rmTransEligible: rmTransEligible;
}

export interface rmTransEligibleRtn {
    rmTransEligible: rmTransEligible;
}

export interface invalidDatesRtn {
    invalidDates: invalidDates[],
    invalidDatesDept: string;
}

export const temprmTimes: rmTimes = {
    RC: '0000',
    BuiltFlag: false,
};

export const temprmTransEligible: rmTransEligible = {
    transLoanerEligible: true,
    transRentalEligible: true,
    transWaiterEligible: true,
    transPPUDEligible: true,
    transPPUDWLEligible: true,
};

/** Initial State */
const initialState: IrmTimesState = {
    rmTimes: temprmTimes,
    rmTimeSlots: [],
    rmTransEligible: temprmTransEligible,
    invalidDates: [],
    invalidDatesDept: '',
};

export const RMTimesSlice = createSlice({
    name: 'RMTimes',
    initialState: initialState as IrmTimesState,
    reducers: {
        receiveRMTimes: (state, action: PayloadAction<rmTimesRtn>) => {
            state.rmTimeSlots = action.payload.rmTimeSlots;
            state.rmTimes = action.payload.rmTimes;
            state.rmTransEligible = action.payload.rmTransEligible;
        },
        clearRMTimes: (state) => {
            state.rmTimeSlots = [];
            state.rmTimes = temprmTimes;
        },
        getInvalidDates: (state, action: PayloadAction<invalidDatesRtn>) => {
            state.invalidDates = action.payload.invalidDates;
            state.invalidDatesDept = action.payload.invalidDatesDept;
        },
        clearInvalidDates: (state) => {
            state.invalidDates = [];
            state.invalidDatesDept = '';
        },
        setRMTimesError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        },
    },
});

export const {
    receiveRMTimes,
    clearRMTimes,
    getInvalidDates,
    clearInvalidDates,
    setRMTimesError,
} = RMTimesSlice.actions;

export default RMTimesSlice.reducer;

export function GetRMTimes(specProfile: string, selectedDate?: string, selectedAdv?: string, recallSelected?: boolean) {
    // return async (dispatch: (action: ActionRMTimes) => void) => {
    return async (dispatch, getState: () => IStoreState) => {
        const CurrAppt = { ...getState().Appointment.Appointment };
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const apptOps = getState().Appointment.ApptOps.map(op => op.operation);
        const logId = getState().Framework.logId;
        const menus = getState().Menus;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }

        // for each apptOp that is null or undefined replace it with a WYO marker
        apptOps.forEach((op) => {
            if (op === null || op === undefined) {
                apptOps[apptOps.indexOf(op)] = 'WYO';
            }
        });

        menus.checkedConditionalMenus.forEach((menu) => {
            const menuIndex = menus.allMenus.menus.findIndex(result => result.id === menu.id);
            if (menus.allMenus.menus[menuIndex].eraOpCode) {
                apptOps.push(menus.allMenus.menus[menuIndex].eraOpCode);
            }
        });

        if (menus.checkedMileageMenu) {
            const menuIndex = menus.allMenus.menus.findIndex(result => result.id === menus.checkedMileageMenu);
            apptOps.push(menus.allMenus.menus[menuIndex].eraOpCode);
        }

        const reqData = {
            profileNum: specProfile,
            dept: CurrAppt.department,
            selectedAdv: selectedAdv !== undefined ? selectedAdv : '',
            selectedDate: selectedDate !== undefined ? selectedDate : '',
            selectedTrans: '',
            recallSelected: recallSelected !== undefined ? recallSelected : '',
            apptOps: apptOps,
            logId: logId,
            callingProgram: callingProgram,
        };

        let response = await acBase.eraWebServiceCall<rmTimeRec[]>('SVPController', 'RMTimes', reqData, store, branch);
        if (response?.returnCode && response?.returnCode === '8000') {
            dispatch(setRMTimesError(response.returnCode));
        } else {
            let rmTest = {
                // BuiltTime: Date.parse(Date()),
                RC: '1000',
                BuiltFlag: false,
            };
            if (response === null) {
                response = {};
            }
            if (response && !(response as AxiosError).isAxiosError) {
                rmTest = {
                    // BuiltTime: Date.parse(Date()),
                    RC: '1000',
                    BuiltFlag: true,
                };
            } else {
                response = {};
            }

            let {transWaiterEligible, transLoanerEligible, transRentalEligible, transPPUDEligible, transPPUDWLEligible,
                rmTimes} = response;

            transWaiterEligible = transWaiterEligible ?? true;
            transLoanerEligible = transLoanerEligible ?? true;
            transRentalEligible = transRentalEligible ?? true;
            transPPUDEligible = transPPUDEligible ?? true;
            transPPUDWLEligible = transPPUDWLEligible ?? true;
            rmTimes = rmTimes ?? [];

            const rtnRMTimes: rmTimesRtn = {
                rmTimes: rmTest,
                rmTimeSlots: rmTimes,
                rmTransEligible: {
                    transLoanerEligible,
                    transWaiterEligible,
                    transRentalEligible,
                    transPPUDEligible,
                    transPPUDWLEligible,
                },
            };
            dispatch(receiveRMTimes(rtnRMTimes));
        }
    };
}

export function ClearRMTimes() {
    return (dispatch) => {
        dispatch(clearRMTimes());
    };
}

export function GetInvalidDates(specProfile: string, selectedDate?: string, selectedAdv?: string, recallSelected?: boolean) {
    // return async (dispatch: (action: ActionRMTimes) => void) => {
    return async (dispatch, getState: () => IStoreState) => {
        const CurrAppt = { ...getState().Appointment.Appointment };
        const apptOps = getState().Appointment.ApptOps.map(op => op.operation);
        const Specs = { ...getState().Specs };
        const defaultDept = Specs.specs?.departmentFlag ? Specs.specs?.departmentFlag : '';
        let inpDept = CurrAppt.department;
        if (inpDept === '') {
            inpDept = defaultDept;
        }
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Framework.logId;
        const menus = getState().Menus;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }

        menus.checkedConditionalMenus.forEach((menu) => {
            const menuIndex = menus.allMenus.menus.findIndex(result => result.id === menu.id);
            if (menus.allMenus.menus[menuIndex].eraOpCode) {
                apptOps.push(menus.allMenus.menus[menuIndex].eraOpCode);
            }
        });

        if (menus.checkedMileageMenu) {
            const menuIndex = menus.allMenus.menus.findIndex(result => result.id === menus.checkedMileageMenu);
            apptOps.push(menus.allMenus.menus[menuIndex].eraOpCode);
        }

        const reqData = {
            profileNum: specProfile,
            dept: inpDept,
            selectedAdv: selectedAdv !== undefined ? selectedAdv : '',
            selectedDate: selectedDate !== undefined ? selectedDate : '',
            selectedTrans: '',
            recallSelected: recallSelected,
            apptOps: apptOps,
            logId: logId,
            callingProgram: callingProgram,
        };

        const response = await acBase.eraWebServiceCall<rmTimeRec[]>('SVPController', 'GetInvalidDates', reqData, store, branch);

        if (response?.returnCode && response?.returnCode) {
            dispatch(setRMTimesError(response.returnCode));
        } else {
            const rtnDates: invalidDatesRtn = {
                invalidDates: response,
                invalidDatesDept: inpDept,
            };

            dispatch(getInvalidDates(rtnDates));
        }
    };
}

export function ClearInvalidDates() {
    return (dispatch) => {
        dispatch(clearInvalidDates());
    };
}
